import api from '../lib/axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

export interface CreateTopupRequest {
  paymentMethod: 'mm_qr' | 'aplus';
  amount: number;
  currency: string;
  description: string;
}

export interface CreateTopupResponse {
  success: boolean;
  data: {
    transactionId: string;
    qrCode?: string; // Legacy/alt field
    paymentQrCode?: string; // For MMQR/APlus
    paymentUrl?: string;
    params?: Record<string, unknown>;
    // APlus specific fields
    pushNotiLink?: string;
    deepLink?: string;
    paymentId?: string;
  };
  message?: string;
}

export interface TopupStatusResponse {
  success: boolean;
  data: {
    status: 'PENDING' | 'COMPLETED' | 'FAILED';
    transactionId: string;
    walletTransactionId?: string;
    amount: number;
  };
  _status?: number;
}

// Internal types for adapter
interface AdapterResponse {
  success?: boolean;
  data?: {
    topupId?: string;
    paymentQrCode?: string;
    qrCode?: string;
    [key: string]: unknown;
  };
  topupId?: string;
  paymentQrCode?: string;
  [key: string]: unknown;
}

export const paymentService = {
  createTopup: async (payload: CreateTopupRequest): Promise<CreateTopupResponse> => {
    const response = await api.post<unknown>(
      `${API_BASE_URL}/api/v1/payment/topup/create`,
      payload,
    );
    // console.log('[PaymentService] createTopup response:', response.data);

    // Adapter for various response structures
    const raw = response.data as AdapterResponse;

    // Case 1: Standard wrapped response with topupId instead of transactionId
    if (raw.success && raw.data && raw.data.topupId) {
      // Mutate or return new object to match interface
      return {
        ...raw,
        data: {
          ...raw.data,
          transactionId: raw.data.topupId,
          paymentQrCode: raw.data.paymentQrCode || raw.data.qrCode, // normalization
          pushNotiLink: raw.data.pushNotiLink as string | undefined,
          deepLink: raw.data.deepLink as string | undefined,
          paymentId: raw.data.paymentId as string | undefined,
        },
      } as CreateTopupResponse;
    }

    // Case 2: Flat response (legacy checks)
    if (typeof raw.topupId === 'string') {
      return {
        success: true,
        message: 'Success',
        data: {
          transactionId: raw.topupId,
          paymentQrCode: raw.paymentQrCode,
          pushNotiLink: raw.pushNotiLink as string | undefined,
          deepLink: raw.deepLink as string | undefined,
          paymentId: raw.paymentId as string | undefined,
        },
      } as CreateTopupResponse;
    }

    return raw as unknown as CreateTopupResponse;
  },

  checkTopupStatus: async (transactionId: string): Promise<TopupStatusResponse> => {
    const response = await api.get<TopupStatusResponse>(
      `${API_BASE_URL}/api/v1/payment/topup/${transactionId}/status`,
      {
        headers: {
          'Cache-Control': 'no-cache, no-store, max-age=0',
          Pragma: 'no-cache',
          Expires: '0',
        },
        params: {
          _ts: Date.now(),
        },
      },
    );
    // Return both status and data to allow handling 304 Not Modified
    return { ...response.data, _status: response.status };
  },
};
